import {InstallmentsModel} from '../installments/installments/installments.model';
import {TransactionsModalModel} from './transactions.modal/transactions.modal.model';
import {TransactionsModel} from './transactions.model';
import {
    buildCacheKey,
    cacheValue,
    getCachedValueAsync,
    hashPayload,
    isOfflineServiceEnabled,
    removeCachedValue,
} from '../../utils/offline/offline.cache';

export interface CachedTransactionsModalPayload {
    businessUnits?: any[];
    clients?: any[];
    form: any;
    installmentsCreateEditForm: InstallmentsModel;
    paymentMethods?: any[];
    productList: TransactionsModalModel[];
    serviceAccounts: any[];
    users?: any[];
    exchangeRates?: any[];
    services?: any[];
    stocks?: any[];
    modalDictionaryList?: any[];
    clientInfo?: any;
    installmentConditions?: any[];
    installmentLine?: any;
    clientInstallments?: any[];
    cashBackInfo?: any;
    selectedTranche?: any;
    installmentCondition?: any;
    exchangeRate?: number;
    installmentHasCommission?: boolean;
}

export interface TransactionsLookupCriteria {
    [key: string]: string | number | boolean | null | undefined;
}

export interface OfflineTransactionSyncState {
    attempts: number;
    lastAttemptAt: string;
    nextRetryAt: string;
    lastError?: string;
    blocked?: boolean;
    failureKind?: 'connectivity' | 'backend';
    httpStatus?: number;
}

export interface OfflineTransactionSyncError {
    attempt: number;
    at: string;
    message: string;
    failureKind?: 'connectivity' | 'backend';
    httpStatus?: number;
    responseData?: any;
}

interface LookupRecord<T = any> {
    id: string;
    criteria: Record<string, string | number | boolean | null>;
    updatedAt: string;
    value: T;
}

interface LookupTablePayload<T = any> {
    updatedAt: string;
    records: LookupRecord<T>[];
}

export interface OfflineTransactionRecord {
    id: number;
    localOnly: true;
    createdAt: string;
    idempotency_key: string;
    refs: {
        business_unit_id: number;
        client_id: number;
        payment_method_id: number;
        account_id: number;
        type_id: number;
        employee_id: number;
        form_state_id: string;
    };
    requestPayload: any;
    transaction: TransactionsModel;
    sync?: OfflineTransactionSyncState;
    sync_errors?: OfflineTransactionSyncError[];
}

const TRANSACTIONS_OFFLINE_PREFIX = buildCacheKey('transactions', 'offline');

const KEY_FORM_STATE_CREATE_OPERATION = buildCacheKey(
    TRANSACTIONS_OFFLINE_PREFIX,
    'form',
    'state',
    'create_operation'
);

const KEY_OFFLINE_TRANSACTIONS = buildCacheKey(
    TRANSACTIONS_OFFLINE_PREFIX,
    'table',
    'transactions'
);

const LEGACY_TRANSACTION_CACHE_KEYS = [
    buildCacheKey(TRANSACTIONS_OFFLINE_PREFIX, 'table', 'archive_transactions'),
    buildCacheKey(TRANSACTIONS_OFFLINE_PREFIX, 'table', 'list_snapshots'),
    buildCacheKey(TRANSACTIONS_OFFLINE_PREFIX, 'table', 'latest_list'),
    buildCacheKey(TRANSACTIONS_OFFLINE_PREFIX, 'table', 'offline_created'),
];

const OFFLINE_TRANSACTIONS_MAX_RECORDS = 1000;
const OFFLINE_TRANSACTION_SYNC_ERRORS_MAX_RECORDS = 20;
const FORM_STATE_ID = 'create_operation';

export const OFFLINE_TRANSACTIONS_CHANGED_EVENT = 'visor:transactions-offline-changed';

let transactionsOfflineQueue: Promise<void> = Promise.resolve();
let cachedTransactionsModalStateMemory: CachedTransactionsModalPayload | null = null;

const enqueueOfflineWrite = <T>(task: () => Promise<T>) => {
    const nextTask = transactionsOfflineQueue.then(task, task);
    transactionsOfflineQueue = nextTask.then(
        () => undefined,
        () => undefined
    );
    return nextTask;
};

const normalizeCriteria = (
    criteria: TransactionsLookupCriteria = {}
): Record<string, string | number | boolean | null> => {
    const entries = Object.entries(criteria)
        .filter(([, value]) => value !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, value]) => [key, value ?? null] as const);

    return Object.fromEntries(entries);
};

const getCriteriaId = (criteria: TransactionsLookupCriteria = {}) => {
    return hashPayload(normalizeCriteria(criteria));
};

const getLookupTableKey = (table: string) => {
    return buildCacheKey(TRANSACTIONS_OFFLINE_PREFIX, 'form', 'lookup', table);
};

const getLookupRecordKey = (
    table: string,
    criteria: TransactionsLookupCriteria = {}
) => {
    return buildCacheKey(
        TRANSACTIONS_OFFLINE_PREFIX,
        'form',
        'lookup',
        table,
        getCriteriaId(criteria)
    );
};

const getLookupTableAsync = async <T>(table: string) => {
    return (await getCachedValueAsync<LookupTablePayload<T>>(getLookupTableKey(table)))?.value;
};

const getOfflineTransactionsTableAsync = async () => {
    return (await getCachedValueAsync<OfflineTransactionRecord[]>(KEY_OFFLINE_TRANSACTIONS))?.value ?? [];
};

const getRecordSyncErrors = (record: OfflineTransactionRecord) => {
    if (Array.isArray(record.sync_errors) && record.sync_errors.length > 0) {
        return record.sync_errors.slice(-OFFLINE_TRANSACTION_SYNC_ERRORS_MAX_RECORDS);
    }

    if (!record.sync?.lastError) {
        return [];
    }

    return [{
        attempt: record.sync.attempts ?? 0,
        at: record.sync.lastAttemptAt || record.createdAt || new Date().toISOString(),
        message: record.sync.lastError,
        failureKind: record.sync.failureKind,
        httpStatus: record.sync.httpStatus,
    }];
};

const sanitizeOfflineTransactions = (records: OfflineTransactionRecord[]) => {
    const map = new Map<number, OfflineTransactionRecord>();

    records.forEach((record) => {
        if (!record || record.localOnly !== true || !record.transaction) {
            return;
        }

        const idempotencyKey =
            record.idempotency_key ||
            (record as any).idempotencyKey ||
            record.requestPayload?.idempotency_key ||
            record.transaction?.idempotency_key ||
            hashPayload(`${record.id}:${record.createdAt || ''}`);

        map.set(record.id, {
            ...record,
            idempotency_key: idempotencyKey,
            sync_errors: getRecordSyncErrors(record),
            transaction: {
                ...record.transaction,
                idempotency_key: idempotencyKey,
            },
        });
    });

    return Array.from(map.values())
        .sort((left, right) => right.id - left.id)
        .slice(0, OFFLINE_TRANSACTIONS_MAX_RECORDS);
};

const overwriteLegacyTransactionCaches = async () => {
    await Promise.all(
        LEGACY_TRANSACTION_CACHE_KEYS.map((key) => removeCachedValue(key))
    );
};

const notifyOfflineTransactionsChanged = () => {
    if (typeof window === 'undefined') {
        return;
    }

    window.dispatchEvent(new Event(OFFLINE_TRANSACTIONS_CHANGED_EVENT));
};

export const transactionsOfflineInit = async () => {
    if (!isOfflineServiceEnabled()) {
        return;
    }

    await enqueueOfflineWrite(async () => {
        const offlineTransactions = await getOfflineTransactionsTableAsync();
        const sanitized = sanitizeOfflineTransactions(offlineTransactions);

        if (sanitized.length > 0) {
            await cacheValue(KEY_OFFLINE_TRANSACTIONS, sanitized);
        } else {
            await removeCachedValue(KEY_OFFLINE_TRANSACTIONS);
        }

        await overwriteLegacyTransactionCaches();
    });
};

export const cacheTransactionsModalState = async (payload: CachedTransactionsModalPayload) => {
    if (!isOfflineServiceEnabled()) {
        return;
    }

    await enqueueOfflineWrite(async () => {
        const current = cachedTransactionsModalStateMemory;
        const nextValue: CachedTransactionsModalPayload = {
            ...current,
            ...payload,
            form: payload.form ?? current?.form ?? {},
            installmentsCreateEditForm:
                payload.installmentsCreateEditForm ?? current?.installmentsCreateEditForm,
            productList: payload.productList ?? current?.productList ?? [],
            serviceAccounts: payload.serviceAccounts ?? current?.serviceAccounts ?? [],
        };
        cachedTransactionsModalStateMemory = nextValue;

        await cacheValue(KEY_FORM_STATE_CREATE_OPERATION, {
            id: FORM_STATE_ID,
            updatedAt: new Date().toISOString(),
            value: nextValue,
        });
    });
};

export const getCachedTransactionsModalStateAsync = async () => {
    if (!isOfflineServiceEnabled()) {
        return undefined;
    }

    if (cachedTransactionsModalStateMemory) {
        return cachedTransactionsModalStateMemory;
    }

    const cached = await getCachedValueAsync<{
        id: string;
        updatedAt: string;
        value: CachedTransactionsModalPayload;
    }>(KEY_FORM_STATE_CREATE_OPERATION);

    const nextValue = cached?.value?.value ?? null;
    cachedTransactionsModalStateMemory = nextValue;
    return nextValue ?? undefined;
};

export const hasMeaningfulTransactionsModalState = (payload: CachedTransactionsModalPayload) => {
    return Boolean(
        payload.productList?.length ||
        payload.serviceAccounts?.length ||
        payload.form?.details ||
        (payload.form?.client_id && payload.form.client_id !== 1) ||
        (payload.form?.paying_client_id && payload.form.paying_client_id !== 1) ||
        (payload.form?.payment_method_id && payload.form.payment_method_id !== 1) ||
        (payload.form?.employee_id && payload.form.employee_id !== 0) ||
        (payload.form?.account_id && payload.form.account_id !== 1) ||
        (payload.form?.type_id && payload.form.type_id !== 1) ||
        (payload.form?.bu_id && payload.form.bu_id !== 0) ||
        payload.form?.deadline_text ||
        payload.form?.account ||
        Number(payload.form?.cashback_amount || 0) > 0 ||
        (payload.installmentsCreateEditForm?.condition_id && payload.installmentsCreateEditForm.condition_id !== 0) ||
        (payload.installmentsCreateEditForm?.installment_line_id && payload.installmentsCreateEditForm.installment_line_id !== 0) ||
        Number(payload.installmentsCreateEditForm?.prepaid_amount || 0) > 0 ||
        Number(payload.installmentsCreateEditForm?.commission_amount || 0) > 0 ||
        Boolean(payload.installmentsCreateEditForm?.start_schedule)
    );
};

export const cacheTransactionsLookup = async <T>(
    table: string,
    criteria: TransactionsLookupCriteria,
    value: T
) => {
    if (!isOfflineServiceEnabled()) {
        return;
    }

    await enqueueOfflineWrite(async () => {
        const normalizedCriteria = normalizeCriteria(criteria);
        const recordId = getCriteriaId(criteria);
        const nextRecord: LookupRecord<T> = {
            id: recordId,
            criteria: normalizedCriteria,
            updatedAt: new Date().toISOString(),
            value,
        };

        await cacheValue(getLookupRecordKey(table, criteria), nextRecord);
    });
};

export const getCachedTransactionsLookupAsync = async <T>(
    table: string,
    criteria: TransactionsLookupCriteria
) => {
    if (!isOfflineServiceEnabled()) {
        return undefined;
    }

    const record = await getCachedValueAsync<LookupRecord<T>>(getLookupRecordKey(table, criteria));

    if (record?.value?.value !== undefined) {
        return record.value.value;
    }

    const payload = await getLookupTableAsync<T>(table);
    const recordId = getCriteriaId(criteria);
    return payload?.records.find((record) => record.id === recordId)?.value
        ?? payload?.records?.[0]?.value;
};

export const getOfflineTransactionsAsync = async () => {
    if (!isOfflineServiceEnabled()) {
        return [];
    }

    const records = await getOfflineTransactionsTableAsync();
    return sanitizeOfflineTransactions(records).map((record) => record.transaction);
};

export const getOfflineTransactionRecordsAsync = async () => {
    if (!isOfflineServiceEnabled()) {
        return [];
    }

    const records = await getOfflineTransactionsTableAsync();
    return sanitizeOfflineTransactions(records);
};

export const addOfflineTransactionToCache = async (record: OfflineTransactionRecord) => {
    if (!isOfflineServiceEnabled()) {
        return [];
    }

    return enqueueOfflineWrite(async () => {
        const current = await getOfflineTransactionsTableAsync();
        const next = sanitizeOfflineTransactions([record, ...current]);
        if (next.length > 0) {
            await cacheValue(KEY_OFFLINE_TRANSACTIONS, next);
        } else {
            await removeCachedValue(KEY_OFFLINE_TRANSACTIONS);
        }
        notifyOfflineTransactionsChanged();
        return next;
    });
};

export const removeOfflineTransactionFromCache = async (recordId: number) => {
    if (!isOfflineServiceEnabled()) {
        return [];
    }

    return enqueueOfflineWrite(async () => {
        const current = await getOfflineTransactionsTableAsync();
        const next = sanitizeOfflineTransactions(
            current.filter((record) => Number(record.id) !== Number(recordId))
        );

        if (next.length > 0) {
            await cacheValue(KEY_OFFLINE_TRANSACTIONS, next);
        } else {
            await removeCachedValue(KEY_OFFLINE_TRANSACTIONS);
        }

        notifyOfflineTransactionsChanged();
        return next;
    });
};

export const updateOfflineTransactionSyncState = async (
    recordId: number,
    sync: OfflineTransactionSyncState,
    syncError?: OfflineTransactionSyncError
) => {
    if (!isOfflineServiceEnabled()) {
        return [];
    }

    return enqueueOfflineWrite(async () => {
        const current = await getOfflineTransactionsTableAsync();
        const next = sanitizeOfflineTransactions(
            current.map((record) => {
                if (Number(record.id) !== Number(recordId)) {
                    return record;
                }

                return {
                    ...record,
                    sync,
                    sync_errors: syncError
                        ? [
                            ...(Array.isArray(record.sync_errors) ? record.sync_errors : []),
                            syncError,
                        ].slice(-OFFLINE_TRANSACTION_SYNC_ERRORS_MAX_RECORDS)
                        : record.sync_errors,
                };
            })
        );

        if (next.length > 0) {
            await cacheValue(KEY_OFFLINE_TRANSACTIONS, next);
        } else {
            await removeCachedValue(KEY_OFFLINE_TRANSACTIONS);
        }

        return next;
    });
};
